import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// /api/customer-tasks REST controller (OData değil; ?take= ile liste döner, GET-by-id yok).
// Tek kayıt yükleme: önce location.state.row; yoksa listeyi çekip id ile bul.
const API_BASE = '/api/customer-tasks';

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Açık' },
  { value: 'DONE', label: 'Tamamlandı' },
  { value: 'CANCELLED', label: 'İptal' },
];
const statusLabel = (v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || (v || '-');
const statusTone = (v) => (v === 'DONE' ? 'ok' : v === 'CANCELLED' ? 'neutral' : 'wait');

const EMPTY_FORM = {
  assigneeRepresentativeId: '',
  assigneeName: '',
  customerId: '',
  customerTitle: '',
  title: '',
  description: '',
  assignedByName: '',
  status: 'OPEN',
  completedAt: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return pick(data, 'items', 'Items') ?? [];
}

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.title || item.Title || item.name || item.Name || item.description || item.Description || '';
  return desc ? (code ? `${code} - ${desc}` : desc) : code;
}

function rowToForm(row) {
  const completedAtIso = pick(row, 'completedAt', 'CompletedAt');
  return {
    assigneeRepresentativeId: pick(row, 'assigneeRepresentativeId', 'AssigneeRepresentativeId') || '',
    assigneeName: pick(row, 'assigneeName', 'AssigneeName') || '',
    customerId: pick(row, 'customerId', 'CustomerId') || '',
    customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
    title: pick(row, 'title', 'Title') || '',
    description: pick(row, 'description', 'Description') || '',
    assignedByName: pick(row, 'assignedByName', 'AssignedByName') || '',
    status: pick(row, 'status', 'Status') || 'OPEN',
    completedAt: completedAtIso ? String(completedAtIso).slice(0, 16) : '',
  };
}

export function CustomerTaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Saha Görevi' : 'Saha Görevi Detayı', 'Ofisten saha temsilcisine atanan görev kartı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Tek kayıt yükleme: önce location.state.row; yoksa listeyi çekip id ile bul (GET-by-id yok).
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow) {
      setForm(rowToForm(stateRow));
      // Liste satırı audit alanlarını içermez → tam kaydı arka planda çek (bloklamaz; GET-by-id yok, listeden bul).
      fetch(`${API_BASE}?take=500`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const items = body ? unwrapResponse(body) : [];
          const rec = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Görev alınamadı (${res.status})`);
      const items = unwrapResponse(await res.json());
      const found = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!found) throw new Error('Görev bulunamadı');
      setForm(rowToForm(found));
      setAudit({ createdBy: pick(found, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(found, 'createdDate', 'CreatedDate'), updatedBy: pick(found, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(found, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Görev alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /customer-tasks/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    assigneeRepresentativeId: form.assigneeRepresentativeId,
    customerId: form.customerId || null,
    title: form.title.trim(),
    description: form.description.trim() || null,
    assignedByName: form.assignedByName.trim() || null,
    status: form.status,
    completedAt: form.completedAt ? new Date(form.completedAt).toISOString() : null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.assigneeRepresentativeId) { showToast('error', 'Atanan temsilci zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Görev oluşturuldu. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Görev oluşturuldu.');
        setTimeout(() => navigate('/customer-tasks'), 400);
        return;
      }
      showToast('success', 'Görev güncellendi.');
      setTimeout(() => navigate('/customer-tasks'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/customer-tasks/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Görev silindi.');
      setTimeout(() => navigate('/customer-tasks'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Saha Görevi' : (form.title || 'Saha Görevi Detayı');
  const statusBadge = !isNew && <StatusBadge tone={statusTone(form.status)}>{statusLabel(form.status)}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/customer-tasks')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni görev aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Saha Görevleri', to: '/customer-tasks' }, { label: isNew ? 'Yeni Saha Görevi' : 'Saha Görevi Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'CustomerTask', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="briefcase" title="Görev Bilgileri" subtitle="Atama, müşteri ve görev içeriği">
          <Grid cols={2}>
            <FieldCustom label="Atanan Temsilci" req invalid={tried && !form.assigneeRepresentativeId}>
              <RelationPicker
                config={RELATION_CONFIGS.representative}
                value={form.assigneeRepresentativeId || ''}
                selectedLabel={form.assigneeName}
                onChange={(val, item) => setForm((c) => ({ ...c, assigneeRepresentativeId: val, assigneeName: lookupLabel(item) }))}
                placeholder="Temsilci ara…"
              />
            </FieldCustom>
            <FieldCustom label="Müşteri (opsiyonel)">
              <RelationPicker
                config={RELATION_CONFIGS.customer}
                value={form.customerId || ''}
                selectedLabel={form.customerTitle}
                onChange={(val, item) => setForm((c) => ({ ...c, customerId: val, customerTitle: lookupLabel(item) }))}
                placeholder="(Genel görev)"
              />
            </FieldCustom>
            <FieldText form={form} set={set} tried={tried} k="title" label="Başlık" req span placeholder="Başlık giriniz" />
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={4} span placeholder="Açıklama giriniz" />
          </Grid>
        </Section>

        <Section icon="info" title="Durum & Atama" subtitle="Görev durumu ve tamamlanma bilgisi">
          <Grid cols={3}>
            <FieldText form={form} set={set} k="assignedByName" label="Görevi Veren" placeholder="örn. Bölge Müdürü" />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />
            <FieldText form={form} set={set} k="completedAt" label="Tamamlanma" type="datetime-local" />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title || 'Görev'}”</strong> görevini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default CustomerTaskDetailPage;
